import React, { useMemo } from 'react';
import { groupBy, sumBy } from 'lodash';
import { useSubscription } from '@apollo/client';
import { IconArrowLink, IconEco, IconWarning } from '../../../../../../assets/icons';
import { useInstallationContext } from '../../../../../../context/installation';
import {
  MeasurementChannelKind,
  MeasurementSubscriptionResponse,
  OnDashboardMeasurementSubscription,
  OnDashboardMeasurementSubscriptionVariables,
  OptimizerVectorParameterTypeInternal,
} from '../../../../../../data-access/gql-types/graphql';
import { ON_DASHBOARD_MEASUREMENT } from '../../../../../../data-access/subscriptions/on-measurement';
import { useMeasurementMapOperations } from '../../../../hooks/measurement-map/use-measurement-map-operations';
import { measurementParametersOptimizer } from '../../../../modules/channel-details/measurements/utils';
import { OptimizerCurrentDataMap } from '../../../../modules/channel-details/optimizer/current/types';
import { PhaseState, getOptimizerCurrentState } from '../../../../modules/channel-details/optimizer/utils';
import { ChannelType } from '../../../../types';
import { ChannelOptimizerType } from '../../../../types/channel/optimizer';
import './index.scss';

type OptimizerControlProps = {
  channel: ChannelType;
};

export const OptimizerControl: React.FC<OptimizerControlProps> = ({ channel }) => {
  const { skipLavvaFetch } = useInstallationContext();
  const { convertNumberToMeasurement } = useMeasurementMapOperations();
  const supportedScalars = (channel.data as ChannelOptimizerType).supportedScalarParameters;
  const supportedVectors = (channel.data as ChannelOptimizerType).supportedVectorParameters;

  const { data } = useSubscription<OnDashboardMeasurementSubscription, OnDashboardMeasurementSubscriptionVariables>(
    ON_DASHBOARD_MEASUREMENT,
    {
      variables: {
        input: {
          channelId: channel?.id,
          deviceId: channel?.deviceId,
          kind: MeasurementChannelKind.Optimizer,
          scalarParameterTypes: supportedScalars,
          vectorParameterTypes: supportedVectors,
          indices: [],
        },
      },
      skip: !channel || skipLavvaFetch,
    },
  );

  const optimizerData = useMemo(() => {
    if ((data?.onDashboardMeasurement.data as MeasurementSubscriptionResponse)?.vectorMeasurements) {
      const mapData = (data?.onDashboardMeasurement.data as MeasurementSubscriptionResponse).vectorMeasurements.map(
        (x) => ({
          index: x.index,
          value: x.value,
          type: convertNumberToMeasurement(MeasurementChannelKind.Optimizer)('vectors', x.type),
        }),
      );

      return groupBy(mapData, (x) => x.type) as Record<OptimizerVectorParameterTypeInternal, OptimizerCurrentDataMap[]>;
    }
  }, [data]);

  const value = optimizerData ? sumBy(optimizerData?.ACTIVE_POWER, 'value') : undefined;

  const currentState = useMemo(() => {
    if (value && optimizerData) {
      const over = optimizerData.VOLTAGE.find((x) => getOptimizerCurrentState(x.value).label === PhaseState.Over);
      if (over) return <IconWarning fillRed />;
      else {
        const warning = optimizerData.VOLTAGE.find(
          (x) => getOptimizerCurrentState(x.value).label === PhaseState.Warning,
        );
        if (warning) return <IconWarning fillYellow />;
        else return value < 0 ? <IconEco /> : null;
      }
    }
  }, [value, optimizerData]);

  return (
    <div className="optimizer-control">
      {value !== undefined ? (
        <div className="optimizer-control__value-wrapper">
          <div className="current-icon">{currentState}</div>
          <span className="optimizer-control__value">
            {(Math.abs(value) / 1000).toFixed(measurementParametersOptimizer.ACTIVE_POWER.fixed)}
          </span>
          <span className="optimizer-control__unit">{measurementParametersOptimizer.ACTIVE_POWER.unit}</span>
          {value ? <IconArrowLink size={22} direction={value < 0 ? 'up' : 'down'} /> : null}
        </div>
      ) : null}
    </div>
  );
};
